import { useState, useEffect } from 'react';
import {
  BookingListAPIElementContentModel,
  BookingListAPIPropertiesModel,
  ReservationSummaryDto,
} from '@/services/platform/api/model';
import {
  cn,
  getFirstName,
  ModuleProperties,
  useAuthStore,
  useClientTranslation,
  useLocaleStore,
} from '@/lib/core';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { getUpcomingReservations } from '@/services/platform/data/booking';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { BookingList } from '@/components/shared/booking';
import { Heading } from '@/components/ui/heading';

export const BookingListAPI = (props: ModuleProperties) => {
  const content = props.content as BookingListAPIElementContentModel | undefined;
  const { user } = useAuthStore.getState();
  const locale = useLocaleStore(state => state.locale);
  const hasActiveMembership = user?.hasActiveMembership;
  const t = useClientTranslation();

  if (!content) return null;

  const properties = content.properties as BookingListAPIPropertiesModel;

  const [reservations, setReservations] = useState<ReservationSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUpcomingReservations({ take: 20 }, locale)
      .then(data => setReservations(data ?? []))
      .catch(() => setReservations([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['step__booking-list']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <div className="flex flex-1 flex-col px-4 pt-4 pb-[calc((env(safe-area-inset-bottom)+1.5rem)+96px)]">
        {/* Title */}
        {properties?.title && (
          <Heading
            as="h1"
            className="font-pp-neue-montreal text-jncquoi-neutral-1000 mb-8 text-[24px] font-semibold tracking-[0.5px]"
          >
            {properties.title}
          </Heading>
        )}
        {/* List */}
        {loading ? (
          <SpinLoading wrapperClassName="min-h-[30vh]" />
        ) : (
          <BookingList reservations={reservations} context={props?.context} />
        )}
        {/* Assistance */}
        {(properties?.assistanceTitle || properties?.assistanceDescription) && (
          <div className="mt-auto flex flex-col gap-4 pt-10">
            {properties.assistanceTitle && (
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                {properties.assistanceTitle} {user?.name && <strong>{getFirstName(user?.name)}?</strong>}
              </p>
            )}
            {properties.assistanceDescription && (
              <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] leading-normal tracking-[0.25px]">
                {properties.assistanceDescription.replace(
                  '{{concierge}}',
                  hasActiveMembership
                    ? t('App.Labels.AssistanceConciergeLabel')
                    : t('App.Labels.AssistanceCustomerServiceLabel')
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </ModuleWrapper>
  );
};
